import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from './hooks/useAuth';
import { useToast } from './hooks/useToast';
import { api, downloadExport } from './api/client';
import { loginRequest } from './api/msalConfig';
import type { User } from './types';
import { OrderPage } from './pages/OrderPage';
import { MyOrdersPage } from './pages/MyOrdersPage';
import { AdminPage } from './pages/AdminPage';
import { PickupPage } from './pages/PickupPage';
import { HelpPage } from './pages/HelpPage';
import { ToastContainer } from './components/Toast';
import { useSettings } from './contexts/SettingsContext';

type TabId = 'order' | 'my-orders' | 'admin';

const TABS: { id: TabId; label: string; shortLabel: string; icon: string; path: string }[] = [
  { id: 'order', label: 'Essen bestellen', shortLabel: 'Bestellen', icon: '🍽️', path: '/order' },
  { id: 'my-orders', label: 'Meine Bestellungen', shortLabel: 'Meine', icon: '📋', path: '/my-orders' },
  { id: 'admin', label: 'Admin', shortLabel: 'Admin', icon: '⚙️', path: '/admin' },
];

function AppHeader({
  currentUser,
  activeTab,
  onTabChange,
  onLogout,
  addToast,
}: {
  currentUser: User | null;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  onLogout: () => void;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const helpActive = location.pathname.startsWith('/help');

  const now = new Date();
  const [companyExportMonth, setCompanyExportMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [companyExportLoading, setCompanyExportLoading] = useState(false);

  const handleDownloadCompanyOrders = async () => {
    setCompanyExportLoading(true);
    try {
      await downloadExport(`/export/company-orders?month=${companyExportMonth}`);
      addToast('Mitarbeiterliste wird heruntergeladen', 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setCompanyExportLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = currentUser
    ? `${currentUser.firstName[0] || ''}${currentUser.lastName[0] || ''}`.toUpperCase()
    : '?';

  const visibleTabs = currentUser?.isAdmin ? TABS : TABS.filter((t) => t.id !== 'admin');

  return (
    <>
    <header className="app-header">
      <div className="header-inner">
        <div className="logo">
          <div className="logo-title">Foody Leipzig</div>
          <div className="logo-powered">
            <span>powered by</span>
            <img src="/hypoport-hub.svg" alt="hypoport hub" className="logo-powered-img" />
          </div>
        </div>

        <nav className="tab-pills">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-pill ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <Link to="/help" className={`help-btn ${helpActive ? 'active' : ''}`} title="Hilfe">?</Link>

        <div className="user-menu-wrapper" ref={dropdownRef}>
          <button
            className="user-trigger"
            onClick={() => setDropdownOpen((v) => !v)}
            title={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Benutzer'}
          >
            {currentUser && (
              <span className="user-name">
                {currentUser.firstName} {currentUser.lastName}
              </span>
            )}
            <span className="user-avatar-btn">{initials}</span>
          </button>

          {dropdownOpen && currentUser && (
            <div className="user-dropdown">
              <div className="user-dropdown-info">
                <div className="user-dropdown-name">
                  {currentUser.firstName} {currentUser.lastName}
                </div>
                <div className="user-dropdown-email">{currentUser.email}</div>
                <div style={{ fontSize: '0.719rem', color: 'var(--text-lighter)', marginTop: '0.125rem' }}>
                  {currentUser.company}
                </div>
                {currentUser.pickupCode && (
                  <div style={{ fontSize: '0.75rem', marginTop: '0.375rem', fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)' }}>
                    Code: {currentUser.pickupCode}
                  </div>
                )}
              </div>
              {(currentUser.isCompanyContact || currentUser.isAdmin) && (
                <div style={{ padding: '0.625rem 1rem', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.375rem' }}>
                    Mitarbeiterliste ({currentUser.company})
                  </div>
                  <input
                    type="month"
                    value={companyExportMonth}
                    onChange={(e) => setCompanyExportMonth(e.target.value)}
                    className="admin-filter-input"
                    style={{ marginBottom: '0.375rem', width: '100%' }}
                  />
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ width: '100%' }}
                    onClick={handleDownloadCompanyOrders}
                    disabled={companyExportLoading}
                  >
                    {companyExportLoading ? 'Wird geladen…' : '⬇ Als CSV herunterladen'}
                  </button>
                </div>
              )}
              <button className="user-dropdown-item danger" onClick={onLogout}>
                <span>⟵</span>
                <span>Abmelden</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>

    <nav className="bottom-nav">
      {visibleTabs.map((tab) => (
        <button
          key={tab.id}
          className={`bottom-nav-item ${activeTab === tab.id ? 'active' : ''}`}
          onClick={() => onTabChange(tab.id)}
        >
          <span className="bottom-nav-icon">{tab.icon}</span>
          <span className="bottom-nav-label">{tab.shortLabel}</span>
        </button>
      ))}
    </nav>
    </>
  );
}

function LoginPage() {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen';
      if (!message.includes('cancelled') && !message.includes('user_cancelled')) {
        setError(message);
      }
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">Foody 🍽</div>
        <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-primary)' }}>
          Willkommen
        </div>
        <div className="login-tagline">
          Mittagstischbestellung für deine Firma.
        </div>
        {error && (
          <div className="info-banner error" style={{ textAlign: 'left', width: '100%' }}>
            <span className="info-banner-icon">⚠</span>
            <span>{error}</span>
          </div>
        )}
        <button
          className="btn btn-primary btn-lg"
          style={{ width: '100%', marginTop: '0.5rem' }}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? 'Wird angemeldet…' : 'Mit Microsoft anmelden'}
        </button>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-lighter)' }}>
          Melde dich mit deinem Firmen-Account an
        </div>
        <div className="login-powered">
          <span>powered by</span>
          <img src="/hypoport-hub.svg" alt="hypoport hub" className="login-powered-img" />
        </div>
      </div>
    </div>
  );
}

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading: authLoading, apiError, accessDenied, retryApi, logout } = useAuth();
  const { toasts, addToast, removeToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  const getActiveTab = (): TabId => {
    if (location.pathname.startsWith('/my-orders')) return 'my-orders';
    if (location.pathname.startsWith('/admin')) return 'admin';
    return 'order';
  };

  const handleTabChange = (tab: TabId) => {
    const found = TABS.find((t) => t.id === tab);
    if (found) navigate(found.path);
  };

  useEffect(() => {
    if (currentUser && !usersLoaded) {
      api.users.getAll().then((data) => {
        setUsers(data);
        setUsersLoaded(true);
      }).catch(() => {});
    }
  }, [currentUser, usersLoaded]);

  if (authLoading) {
    return (
      <div className="loading-center" style={{ minHeight: '100vh' }}>
        <div className="spinner" />
        <span>Wird geladen…</span>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="loading-center" style={{ minHeight: '100vh', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontSize: '2rem' }}>🔒</div>
        <div style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--text-primary)' }}>Kein Zugang</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center', maxWidth: '20rem' }}>
          Dein Unternehmen ist nicht für Foody freigeschaltet. Bitte wende dich an deinen Administrator.
        </div>
        <button className="btn btn-secondary" onClick={logout}>Abmelden</button>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="loading-center" style={{ minHeight: '100vh', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontSize: '2rem' }}>⚠️</div>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Server nicht erreichbar</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center', maxWidth: '18rem' }}>
          Du bist eingeloggt, aber der Server antwortet gerade nicht. Bitte versuche es nochmal.
        </div>
        <button className="btn btn-primary" onClick={retryApi}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!currentUser) {
    return <LoginPage />;
  }

  return (
    <div className="app-wrapper">
      <AppHeader
        currentUser={currentUser}
        activeTab={getActiveTab()}
        onTabChange={handleTabChange}
        onLogout={logout}
        addToast={addToast}
      />

      <main className="app-content">
        <Routes>
          <Route
            path="/"
            element={
              <OrderPage
                currentUser={currentUser}
                users={users}
                addToast={addToast}
              />
            }
          />
          <Route
            path="/order"
            element={
              <OrderPage
                currentUser={currentUser}
                users={users}
                addToast={addToast}
              />
            }
          />
          <Route
            path="/my-orders"
            element={
              <MyOrdersPage
                currentUser={currentUser}
                users={users}
                addToast={addToast}
              />
            }
          />
          <Route
            path="/admin"
            element={
              <AdminPage
                currentUser={currentUser}
                addToast={addToast}
              />
            }
          />
          <Route path="/help" element={<HelpPage />} />
        </Routes>
      </main>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

function PickupRoute() {
  const { pickupEnabled } = useSettings();
  if (!pickupEnabled) return <Navigate to="/" replace />;
  return <PickupPage />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/pickup" element={<PickupRoute />} />
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
}
